import { TranslationsContext, useIsMobileHook } from '@telia-common-components/shared';
import * as React from 'react';
import { Carousel, CarouselItem, Container, EqualHeightProvider, Skeleton, SkeletonItem } from 'telia-front-react';
import { FetchStatus } from '../../../util/FetchStatus';
import { ExceptionCard } from './card/ExceptionCard';
import InternetOfferCard from './card/InternetOfferCard';
import { useInternetCalculator } from './InternetCalculatorProvider';
import { useInternetNotices } from './InternetNoticesProvider';
import { NoResultsNotice } from './notice/NoResultsNotice';
import { findClosestMatch, selectedUseCasesContainSmallDevices } from './suggest/InternetSuggestSpeedUtil';
import { getCustomerIdFromAuth } from './util/InternetCalculatorUtil';
import { FIX_4G_EXCEPTION_CARD_OFFER_CODE, getInternetOffers } from './util/InternetOfferUtil';
import SpecialRequestCard from './securenet/SpecialRequestCard';
import { useInternetOffers } from './InternetOffersProvider';
export const INTERNET_OFFERS_DIV_ID = 'internet-offers';
const OfferCarousel = () => {
    var _a, _b, _c;
    const { calculatorState, addressProductsState } = useInternetOffers();
    const { suggestSpeedState, isSsoLoaded, auth, internetCalculatorConfigState, isSecureNet } = useInternetCalculator();
    const { hasLegalOrServiceMandateState, isCreditAllowedFetching } = useInternetNotices();
    const { translate, translations } = React.useContext(TranslationsContext);
    const fetchFinished = [FetchStatus.FETCHED, FetchStatus.ERROR].includes(calculatorState.status);
    const addressOffers = (((_a = calculatorState.internetOffers) === null || _a === void 0 ? void 0 : _a.plannedInternetCards) || [])
        .concat((_b = calculatorState.internetOffers) === null || _b === void 0 ? void 0 : _b.internetOfferCards)
        .sort((a, b) => Number(b.downloadSpeed) - Number(a.downloadSpeed));
    const { isMobile } = useIsMobileHook();
    const CLOSEST_MATCH_RECOMMENDED_INTERNET_SPEED = isSecureNet ? 155 : 200;
    const internetOffers = getInternetOffers(calculatorState, internetCalculatorConfigState).sort((a, b) => Number(b.downloadSpeed) - Number(a.downloadSpeed));
    const calculatedSuggestion = suggestSpeedState.suggestedOffer;
    const recommendedOffering = calculatedSuggestion ||
        findClosestMatch(CLOSEST_MATCH_RECOMMENDED_INTERNET_SPEED, internetOffers.filter((e) => !e.properties.includes('PLANNED')));
    const recommendedOffer = addressOffers.find((e) => e.productOfferingCode === recommendedOffering);
    const recommendedInternetIndex = !!calculatedSuggestion && (addressOffers === null || addressOffers === void 0 ? void 0 : addressOffers.indexOf(recommendedOffer));
    const customerId = getCustomerIdFromAuth(auth);
    const shouldShowSkeleton = !(translations === null || translations === void 0 ? void 0 : translations.iocc) ||
        calculatorState.status === FetchStatus.FETCHING ||
        !isSsoLoaded ||
        (customerId && hasLegalOrServiceMandateState.status === FetchStatus.FETCHING) ||
        (!!customerId && isCreditAllowedFetching()) ||
        internetCalculatorConfigState.status === FetchStatus.FETCHING ||
        (!isSecureNet ? (addressProductsState === null || addressProductsState === void 0 ? void 0 : addressProductsState.status) === FetchStatus.FETCHING : false);
    const renderOffers = () => {
        const elements = (selectedUseCasesContainSmallDevices(suggestSpeedState.selectedUseCases)
            ? internetOffers
            : internetOffers.filter((offer) => !offer.properties.includes('HIDDEN_SECURE_NET_OFFER'))).map((offer) => (React.createElement(CarouselItem, { key: `internet-offer-${offer.id}` }, offer.productOfferingCode === FIX_4G_EXCEPTION_CARD_OFFER_CODE ? (React.createElement(ExceptionCard, { offer: offer })) : (React.createElement(InternetOfferCard, { offer: offer, isRecommended: offer.productOfferingCode === recommendedOffering, suggestedInternet: suggestSpeedState.suggestedOffer })))));
        isSecureNet && elements.push(React.createElement(CarouselItem, { key: 'internet-offer-special-request-card' }, React.createElement(SpecialRequestCard, null)));
        return elements;
    };
    if (fetchFinished && isSsoLoaded && calculatorState.address && !internetOffers.length) {
        return (React.createElement(Container, null,
            React.createElement(NoResultsNotice, { isError: calculatorState.status === FetchStatus.ERROR })));
    }
    return (React.createElement("div", { id: INTERNET_OFFERS_DIV_ID },
        React.createElement(EqualHeightProvider, { groups: ['card-text', 'price', 'card-footer'], offSet: 10 },
            React.createElement(Carousel, { key: calculatorState.address
                    ? `${calculatorState.address.id}-${calculatorState.address.buildingId}-${((_c = calculatorState.room) === null || _c === void 0 ? void 0 : _c.roomId) || 'no-room'}`
                    : 'carousel-default-internet-offers', nextArrowText: translate('carousel.next'), prevArrowText: translate('carousel.previous'), hasContainer: true, arrows: !isMobile, align: "left", activeItemIndex: calculatedSuggestion ? recommendedInternetIndex : undefined, breakPoint: null, slidesToShow: 1, responsive: [
                    {
                        breakPoint: 'sm',
                        props: { slidesToShow: 2 }
                    },
                    {
                        breakPoint: 'md',
                        props: { slidesToShow: 3 }
                    },
                    {
                        breakPoint: 'lg',
                        props: { slidesToShow: 4 }
                    }
                ] }, shouldShowSkeleton ? renderSkeletonItems(translate) : renderOffers()))));
};
const renderSkeletonItems = (translate) => {
    return [...Array(4).keys()].map((key) => (React.createElement(CarouselItem, { key: key },
        React.createElement(Skeleton, { key: key, label: translate('skeleton.loading') },
            React.createElement(SkeletonItem, { height: '566px' })))));
};
export default OfferCarousel;
